<template>
    <div>
        <input ref="fileInputRef" accept="image/*" capture="environment" class="hidden" type="file" @change="onFileSelected" />

        <div v-if="displayedImageUrl" class="flex flex-col items-center gap-2">
            <div class="relative">
                <img :src="displayedImageUrl" :alt="label" class="max-h-48 rounded-lg object-contain" />
                <div v-if="uploading" class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60">
                    <Spinner size="md" />
                </div>
            </div>
            <button
                :disabled="disabled || uploading"
                class="cursor-pointer text-sm font-medium text-primary underline disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
                @click="retake"
            >
                {{ retakeLabel }}
            </button>
        </div>

        <div v-else>
            <div
                :class="{ 'pointer-events-none cursor-not-allowed opacity-50': disabled || uploading }"
                class="flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
                role="button"
                tabindex="0"
                @click="openCamera"
                @keydown.enter="openCamera"
                @keydown.space.prevent="openCamera"
            >
                <div class="flex items-center gap-2">
                    <svg class="size-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span v-if="uploading">{{ uploadingLabel }}</span>
                    <span v-else>{{ label }}</span>
                </div>
                <span v-if="hint && !uploading" class="text-center text-sm font-normal text-gray-500">{{ hint }}</span>
            </div>
        </div>

        <div v-if="error" class="mt-1 text-sm text-danger">{{ error }}</div>
    </div>
</template>
<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { $http } from '@/bootstrap.js';
import Spinner from '@/Components/Spinner.vue';
import { fileHash } from '@/filehash.js';
import { trans } from '@/mixins/i18n';

const props = defineProps({
    disabled: { type: Boolean, default: false },
    label: { type: String, default: () => trans('general.action.take_photo') },
    hint: { type: String, default: null },
    retakeLabel: { type: String, default: () => trans('general.action.retake_photo') },
    uploadingLabel: { type: String, default: () => trans('general.action.uploading') },
    value: { type: String, default: null },
    // Server-side URL for an already-stored photo (e.g. when reopening a completed task
    // for edit). Falls back to the local blob preview once the operator picks a new file.
    existingUrl: { type: String, default: null },
});

const emit = defineEmits(['change', 'upload-started', 'upload-completed']);

const fileInputRef = ref(null);
const previewUrl = ref(null);
const uploading = ref(false);
const error = ref(null);

// Local blob preview wins over the server-side URL — once the operator picks a new file,
// the new image must show through immediately so the previous photo isn't misleading.
const displayedImageUrl = computed(() => previewUrl.value ?? props.existingUrl);

const openCamera = () => {
    if (props.disabled || uploading.value) return;
    fileInputRef.value.value = '';
    fileInputRef.value.click();
};

const retake = () => {
    if (props.disabled || uploading.value) return;
    revokePreview();
    emit('change', null);
    openCamera();
};

const onFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    error.value = null;
    uploading.value = true;
    emit('upload-started');

    previewUrl.value = URL.createObjectURL(file);

    try {
        const checksum = await fileHash(file);

        const { data } = await $http.post(route('attachments.store'), {
            content_type: file.type,
            size: file.size,
            file_name: file.name,
            checksum,
        });

        const formData = new FormData();
        Object.entries(data.fields).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('file', file);

        await $http.post(data.url, formData);

        emit('change', data.id);
    } catch (e) {
        error.value = e.response?.data?.message || e.message || String(e);
        revokePreview();
        emit('change', null);
    } finally {
        uploading.value = false;
        emit('upload-completed');
    }
};

const revokePreview = () => {
    if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
        previewUrl.value = null;
    }
};

onBeforeUnmount(() => revokePreview());
</script>
